import { useEffect, useState } from "react";
import { RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Tooltip } from "@/components/ui/misc";
import type { UpdateStatus } from "@/types/bridge";

/** Update state from the Electron shell; null in a browser or a shell without the updater. */
export function useUpdateStatus(): UpdateStatus | null {
  const bridge = window.deskBridge;
  const [status, setStatus] = useState<UpdateStatus | null>(null);
  useEffect(() => {
    if (!bridge?.getUpdateStatus) return;
    let live = true;
    void bridge.getUpdateStatus().then((s) => live && setStatus(s));
    const off = bridge.onUpdateStatus?.((s) => setStatus(s));
    return () => {
      live = false;
      off?.();
    };
  }, [bridge]);
  return status;
}

/** Sidebar footer affordance, shown only in Electron once an update has downloaded. */
export function UpdateReady() {
  const status = useUpdateStatus();
  const [restarting, setRestarting] = useState(false);
  if (status?.state !== "ready") return null;
  const restart = () => {
    setRestarting(true);
    window.deskBridge?.installUpdate?.().catch((err: Error) => {
      setRestarting(false);
      toast.error("Couldn't install the update", { description: err.message });
    });
  };
  return (
    <Tooltip content={status.version ? `Desk ${status.version} is ready` : "Update ready"}>
      <button
        type="button"
        disabled={restarting}
        onClick={restart}
        className="desk-no-drag inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md px-2 text-[12px] font-medium text-foreground/85 hover:bg-sidebar-accent hover:text-foreground disabled:opacity-60"
      >
        <RotateCw className="size-3.5 text-info" aria-hidden />
        Restart to update
      </button>
    </Tooltip>
  );
}
